/**
 * Экран «Сегодня» (раздел 8, пункт 1).
 *
 * Первый экран приложения и единственный, который человек открывает, стоя в
 * дверях зала. Отвечает на три вопроса без прокрутки: что сегодня за
 * тренировка, с какими весами и что сказал компаньон.
 *
 * Ни одного числа здесь не считается (продуктовый принцип №1): веса, диапазоны
 * повторов, флаги и текст сообщения приходят готовыми из `GET /today`. Клиент
 * только форматирует их для показа — вес через `formatWeight`, дату через
 * `formatDate`.
 *
 * Офлайн — не исключение, а рабочий режим: зал часто в подвале. Успешный ответ
 * кладётся в кеш, а при обрыве связи показывается он же, но с честной плашкой
 * «данные от такого-то времени» (принцип честности, раздел 2). Отказ сервера
 * (`ApiError`) кешем НЕ подменяется: сервер ответил — значит связь есть, и
 * рисовать вчерашний план вместо его ответа было бы враньём.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError, NetworkError, api } from '../api/client'
import type {
  ExerciseBlock,
  Flag,
  Mesocycle,
  Session,
  Today as TodayData,
} from '../api/types'
import { clearToken } from '../auth/session'
import { haptic, isMiniApp } from '../auth/telegram'
import Reschedule from '../components/Reschedule'
import {
  ConfirmButton,
  Empty,
  ErrorBox,
  Flags,
  Loader,
  formatDate,
  formatWeight,
} from '../components/ui'
import { useAsync } from '../hooks'
import { CACHE_TODAY, readCache, saveCache } from '../offline/db'

/** Данные экрана вместе с пометкой, откуда они взялись. */
interface TodayView {
  data: TodayData
  /** `null` — свежий ответ сервера; число — момент сохранения кеша (мс epoch). */
  cachedAt: number | null
}

/** Параметры пересборки по умолчанию. Уходят на сервер как есть — считает он. */
const REBUILD_PARAMS = { days_per_week: 4, weeks: 5, mode: 'surplus' } as const

/** Сколько находок движка помещается на первый экран, не вытесняя тренировку. */
const FLAG_LIMIT = 3

async function loadToday(): Promise<TodayView> {
  try {
    const data = await api.get<TodayData>('/today')
    try {
      await saveCache(CACHE_TODAY, data)
    } catch {
      // Кеш — страховка, а не условие показа. IndexedDB может быть недоступна
      // (приватное окно, переполненное хранилище) — экран из-за этого падать
      // не должен, данные-то уже на руках.
    }
    return { data, cachedAt: null }
  } catch (e) {
    if (e instanceof NetworkError) {
      const cached = await readCache<TodayData>(CACHE_TODAY).catch(() => null)
      if (cached) return { data: cached.value, cachedAt: cached.savedAt }
    }
    // Либо кеша нет, либо это отказ сервера — и то и другое показывается как
    // ошибка, а не как «всё хорошо, вот твой план».
    throw e
  }
}

/** Дата в локальном поясе как `YYYY-MM-DD` — в таком виде её ждёт `formatDate`. */
function localIsoDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

/**
 * Текст плашки о несвежих данных.
 *
 * Пока речь про сегодня, хватает времени. Если кеш вчерашний — это уже другой
 * разговор, и день называется словами («вчера», «6 сентября»), чтобы «данные от
 * 21:40» нельзя было прочитать как сегодняшние.
 */
function staleLabel(savedAt: number): string {
  const saved = new Date(savedAt)
  const now = new Date()
  const time = saved.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  if (localIsoDate(saved) === localIsoDate(now)) {
    return `Нет связи. Данные от ${time}`
  }
  return `Нет связи. Данные от ${formatDate(localIsoDate(saved), now)}, ${time}`
}

/** «1 подход», «3 подхода», «5 подходов»: кривое склонение читается как баг. */
function plural(n: number, forms: [string, string, string]): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return forms[2]
  const mod10 = n % 10
  if (mod10 === 1) return forms[0]
  if (mod10 >= 2 && mod10 <= 4) return forms[1]
  return forms[2]
}

/**
 * Подсказка «3 подхода × 5–8».
 *
 * Диапазон берётся у первого подхода, а не сводится по всем: движок выдаёт на
 * упражнение одно назначение и штампует им все подходы блока. Сводить границы
 * самим значило бы считать на клиенте (принцип №1).
 */
function setsHint(block: ExerciseBlock): string {
  const first = block.sets[0]
  if (!first) return ''
  const count = block.sets.length
  const reps =
    first.target_reps_min === first.target_reps_max
      ? String(first.target_reps_min)
      : `${first.target_reps_min}–${first.target_reps_max}`
  return `${count} ${plural(count, ['подход', 'подхода', 'подходов'])} × ${reps}`
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'Что-то пошло не так'
}

/**
 * Строка упражнения: что делать и с каким весом.
 *
 * Вес показывается ровно тот, что прислал движок (`suggested_weight`), уже
 * округлённый под доступное железо. `reason` — его же объяснение, почему вес
 * такой (калибровка, разгрузка, откат после паузы): человек имеет право знать,
 * откуда взялась цифра.
 */
function ExerciseRow({ block }: { block: ExerciseBlock }) {
  // Раздел 14: про боль говорим ровно одно — движение исключено из плана.
  // Никаких «поберегись» и «разомнись получше»: это медицинские советы.
  if (block.has_active_pain) {
    return (
      <div className="card tight dim">
        <div className="ex-name">{block.exercise.name}</div>
        <span className="pill warn">исключено из-за боли</span>
        {/* Сервер тут не только констатирует исключение, но и говорит, что с
            этим делать («пересобери программу — движок подберёт замену»).
            Прятать его текст значит оставить человека с пустым местом в плане
            и без выхода. */}
        {block.reason !== '' && (
          <div className="faint" style={{ marginTop: 6 }}>
            {block.reason}
          </div>
        )}
      </div>
    )
  }

  const hint = setsHint(block)

  return (
    <div className="card tight">
      <div className="row between">
        <div style={{ minWidth: 0 }}>
          <div className="ex-name">{block.exercise.name}</div>
          {hint !== '' && <div className="faint">{hint}</div>}
        </div>
        {block.suggested_weight === null ? (
          <div className="ex-weight muted">—</div>
        ) : (
          <div className="ex-weight tnum">
            {formatWeight(block.suggested_weight)}
            <span className="ex-unit"> кг</span>
          </div>
        )}
      </div>
      {block.reason !== '' && (
        <div className="faint" style={{ marginTop: 6 }}>
          {block.reason}
        </div>
      )}
    </div>
  )
}

/**
 * Кнопка сборки программы.
 *
 * Под кнопкой сказано, что именно будет собрано: человек нажимает не на
 * «магию», а на конкретные пять недель по четыре тренировки.
 */
function RebuildButton({ busy, onClick }: { busy: boolean; onClick: () => void }) {
  return (
    <div>
      <button className="btn primary wide big" disabled={busy} onClick={onClick}>
        {busy ? 'Собираю программу…' : 'Составить программу'}
      </button>
      <div className="faint" style={{ marginTop: 8 }}>
        {REBUILD_PARAMS.weeks} недель, {REBUILD_PARAMS.days_per_week} тренировки в неделю,
        режим набора. Упражнения и веса подберёт движок.
      </div>
    </div>
  )
}

/** Ошибка действия и, если помогает повтор, тренировка для второй попытки. */
interface Problem {
  error: unknown
  /**
   * Тренировка, старт которой не дошёл до сервера. Только для `NetworkError`:
   * отказ сервера повтором не чинится, а обрыв связи — чинится.
   */
  retrySession: Session | null
}

/** Ключ в localStorage: подсказку про ярлык показали и её закрыли. */
const INSTALL_HINT_KEY = 'install_hint_dismissed'

/**
 * Одна строка о том, что приложение можно поставить на домашний экран.
 *
 * Показывается ТОЛЬКО внутри Telegram: тому, кто уже открыл приложение
 * ярлыком, предлагать поставить ярлык незачем. Закрывается насовсем — это
 * разовое дело, и висеть над каждой тренировкой оно не должно.
 *
 * Отметка о закрытии живёт в localStorage: потерять её не страшно, подсказка
 * просто появится ещё раз.
 */
function InstallHint() {
  const [hidden, setHidden] = useState(() => {
    try {
      return window.localStorage.getItem(INSTALL_HINT_KEY) === '1'
    } catch {
      return false
    }
  })

  if (hidden || !isMiniApp()) return null

  return (
    <div className="install-hint">
      <Link to="/install">Открывать с домашнего экрана, без Telegram →</Link>
      <button
        className="btn ghost"
        aria-label="Скрыть подсказку"
        onClick={() => {
          setHidden(true)
          try {
            window.localStorage.setItem(INSTALL_HINT_KEY, '1')
          } catch {
            // Приватный режим: подсказка появится снова. Это не поломка.
          }
        }}
      >
        ×
      </button>
    </div>
  )
}

/**
 * Забыть вход на этом устройстве.
 *
 * Называется так, а не «выйти», по двум причинам. Первая: «Выйти» в этом
 * приложении уже занято — так подписан выход с активной тренировки, и два
 * смысла одного слова там, где ошибка стоит похода к боту, недопустимы.
 * Вторая: приложение честно не умеет отзывать выданный токен — он
 * самоподписанный, списка сессий на сервере нет. Стирается ровно то, что
 * лежит на этом телефоне.
 *
 * Живёт в подвале и под подтверждением: нажать случайно нельзя, а искать
 * специально — есть где, экрана настроек в приложении нет.
 */
function ForgetLogin() {
  const [busy, setBusy] = useState(false)

  async function forget(): Promise<void> {
    if (busy) return
    setBusy(true)
    try {
      // Сначала сервер: он гасит куку, которую клиенту не видно и не стереть.
      // Отказ не мешает забыть вход локально — это и есть смысл кнопки.
      await api.post<void>('/auth/logout', undefined, { anonymous: true }).catch(() => {})
    } finally {
      clearToken()
      // Перезагрузка, а не смена состояния: вход — корень всего дерева
      // экранов, и проще начать заново, чем разбирать по кускам.
      window.location.replace('/')
    }
  }

  return (
    <ConfirmButton
      className="btn ghost"
      confirmLabel="Точно забыть вход?"
      onConfirm={() => void forget()}
    >
      Забыть вход на этом устройстве
    </ConfirmButton>
  )
}

export default function Today() {
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsync<TodayView>(loadToday, [])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<Problem | null>(null)
  /** Открыта ли панель переноса тренировки на другую дату. */
  const [moving, setMoving] = useState(false)
  /** Что сказал движок о переносе: сдвиг, сдвоенная дата, дата в прошлом. */
  const [moveFlags, setMoveFlags] = useState<Flag[]>([])

  /** Начать (или продолжить) тренировку: один тап — и человек на экране подходов. */
  async function start(s: Session): Promise<void> {
    if (busy) return
    setBusy(true)
    setProblem(null)
    haptic('medium')
    try {
      // Повторный старт сервер обрабатывает идемпотентно, поэтому «Продолжить»
      // идёт тем же запросом — отдельной ветки не нужно.
      await api.post<Session>(`/sessions/${s.id}/start`)
      navigate(`/workout/${s.id}`)
    } catch (e) {
      // Открыть тренировку «всё равно» нельзя: экран подходов берёт список
      // упражнений из GET /sessions/{id} и офлайн-кеша не имеет — без связи
      // он покажет ту же ошибку. Поэтому при обрыве связи предлагается
      // честный повтор, а не переход в тупик.
      setProblem({ error: e, retrySession: e instanceof NetworkError ? s : null })
    } finally {
      setBusy(false)
    }
  }

  /** Тренировка переехала: экран перечитываем, находки движка показываем. */
  async function moved(flags: Flag[]): Promise<void> {
    setMoving(false)
    setMoveFlags(flags)
    // Перезапрос обязателен: после переноса «ближайшей» может стать другая
    // тренировка, и решает это сервер (`planner.next_session`), а не экран.
    await reload()
  }

  /** Собрать мезоцикл и перечитать экран. */
  async function rebuild(): Promise<void> {
    if (busy) return
    setBusy(true)
    setProblem(null)
    try {
      await api.post<Mesocycle>('/mesocycle/rebuild', REBUILD_PARAMS)
      await reload()
    } catch (e) {
      setProblem({ error: e, retrySession: null })
    } finally {
      setBusy(false)
    }
  }

  // Спиннер — только на первой загрузке. Перезапрос после пересборки не должен
  // сдёргивать с экрана уже показанный план.
  if (loading && !data) {
    return (
      <div className="screen">
        <Loader />
      </div>
    )
  }

  if (!data) {
    const authDead = error instanceof ApiError && error.isAuth
    return (
      <div className="screen">
        <h1>Сегодня</h1>
        <ErrorBox error={error} onRetry={() => void reload()} />
        {authDead && (
          <p className="faint">
            Похоже, сессия закончилась. Открой приложение заново из Telegram или попроси у
            бота новую ссылку командой <b>/login</b>.
          </p>
        )}
      </div>
    )
  }

  const today = data.data
  const session = today.session
  const message = today.message?.trim() ?? ''
  const retrySession = problem?.retrySession ?? null
  // Есть ли что записывать: у исключённого по боли упражнения подходов нет,
  // и тренировка из одних таких блоков — это не тренировка.
  const hasWork = session !== null && session.blocks.some((b) => b.sets.length > 0)

  const problemBox = problem && (
    <div className="card" style={{ borderColor: 'var(--danger)' }}>
      <div style={{ color: 'var(--danger)' }}>{errorText(problem.error)}</div>
      {retrySession !== null && (
        <>
          <div className="faint" style={{ margin: '8px 0 12px' }}>
            Экран подходов берёт план с сервера, поэтому без связи его не открыть. Как
            только связь появится — нажми ещё раз.
          </div>
          <button className="btn wide" disabled={busy} onClick={() => void start(retrySession)}>
            Повторить
          </button>
        </>
      )}
    </div>
  )

  return (
    <div className="screen">
      <InstallHint />

      {data.cachedAt !== null && <div className="stale-note">{staleLabel(data.cachedAt)}</div>}

      {/* Перезапрос упал, но на руках остался прошлый ответ. Показать его молча
          значит выдать старый план за текущий — а после пересборки программы
          это прямая ложь: человек нажал кнопку, экран не изменился, и он думает,
          что ничего не собралось. */}
      {error !== null && (
        <div className="stale-note">
          Не удалось обновить: {errorText(error)}. На экране — прошлый ответ сервера.
        </div>
      )}

      <h1>{capitalize(formatDate(today.date))}</h1>

      {session && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <span className="pill">Неделя {session.week_no}</span>
          {session.is_deload && <span className="pill warn">Разгрузка</span>}
          {session.is_calibration && <span className="pill accent">Калибровка</span>}
        </div>
      )}

      {/* Сообщение дня — лицо продукта (раздел 6). Пустая карточка вместо него
          выглядела бы поломкой, поэтому в отсутствие текста — одна сдержанная
          строка, а не выдуманная бодрость. */}
      {message !== '' ? (
        <div className="card message">{message}</div>
      ) : (
        <div className="card tight">
          <div className="muted">Сообщение придёт позже</div>
          <div className="faint">Компаньон пишет раз в день, когда соберёт свежие цифры.</div>
        </div>
      )}

      {session ? (
        <>
          <h2>{session.label}</h2>
          {/* Сервер объясняет, если показанная тренировка не сегодняшняя:
              просрочена, ещё впереди или вовсе без даты. Спрятать — соврать. */}
          {today.reason !== '' && (
            <div className="faint" style={{ margin: '-4px 0 10px' }}>
              {today.reason}
            </div>
          )}

          {session.blocks.length === 0 ? (
            <Empty
              title="В этой тренировке нет упражнений"
              hint="Собери программу заново — движок подберёт упражнения под доступное железо."
            />
          ) : (
            session.blocks.map((block) => <ExerciseRow key={block.exercise.id} block={block} />)
          )}

          {session.blocks.some((b) => b.has_active_pain) && (
            <div className="faint" style={{ marginBottom: 16 }}>
              Движения с болью движок убрал из плана. Если боль не проходит — покажись
              врачу: приложение не даёт медицинских рекомендаций.
            </div>
          )}

          {/* Записывать нечего — значит и открывать нечего: экран подходов
              покажет пустую тренировку. Вместо кнопки, которая ведёт в никуда,
              стоит та, что чинит причину. */}
          {hasWork ? (
            <button
              className="btn primary wide big"
              disabled={busy}
              onClick={() => void start(session)}
            >
              {busy
                ? 'Открываю…'
                : session.status === 'in_progress'
                  ? 'Продолжить'
                  : 'Начать тренировку'}
            </button>
          ) : (
            <>
              {session.blocks.length > 0 && (
                <div className="faint" style={{ marginBottom: 8 }}>
                  Подходов в этой тренировке не осталось — записывать нечего.
                </div>
              )}
              <RebuildButton busy={busy} onClick={() => void rebuild()} />
            </>
          )}

          {/* Перенос стоит здесь, а не только на экране «Программа»: человек
              открывает «Сегодня», понимает, что сегодня не сможет, и должен
              решить это в том же месте, где увидел проблему. У закрытой
              тренировки дата — факт о сделанной работе, и сервер её не
              двинет (409). */}
          {session.status !== 'completed' &&
            (moving ? (
              <div style={{ marginTop: 12 }}>
                <Reschedule
                  session={session}
                  // Только из СВЕЖЕГО ответа: «сегодня» из кеша может быть
                  // вчерашним, и кнопка поставила бы тренировку во вчера.
                  today={data.cachedAt === null ? today.date : null}
                  onMoved={(result) => void moved(result.flags)}
                  onCancel={() => setMoving(false)}
                />
              </div>
            ) : (
              <button
                className="btn wide"
                style={{ marginTop: 10 }}
                disabled={busy}
                onClick={() => {
                  setMoveFlags([])
                  setMoving(true)
                }}
              >
                Не смогу сегодня — перенести
              </button>
            ))}

          {moveFlags.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Flags flags={moveFlags} />
            </div>
          )}

          {problemBox}
        </>
      ) : (
        <>
          {/* Тренировки нет. Причину знает сервер: программы ещё нет либо
              мезоцикл пройден до конца. Оба случая чинятся пересборкой, поэтому
              кнопка одна и стоит прямо под объяснением. */}
          <div className="card">
            <div style={{ fontSize: 17, marginBottom: 6 }}>Тренировки на сегодня нет</div>
            <div className="muted">{today.reason || 'Сервер не назвал причину'}</div>
          </div>
          <RebuildButton busy={busy} onClick={() => void rebuild()} />
          {problemBox}
        </>
      )}

      {/* Флаги движка: не больше трёх. Порядок задаёт сервер — критичные первыми.
          Об остальных сказано вслух: молча обрезанный список выглядит как
          «у тебя всё в порядке», а это может быть неправдой. */}
      <div style={{ marginTop: 20 }}>
        <Flags flags={today.flags} limit={FLAG_LIMIT} />
        {today.flags.length > FLAG_LIMIT && (
          <div className="faint">
            Показаны {FLAG_LIMIT} находки из {today.flags.length} — сначала самые срочные.
          </div>
        )}
      </div>

      {/* Постоянная ссылка внизу — потому что подсказку наверху закрывают
          насовсем. Без неё крестик уносил бы единственный вход в инструкцию,
          и вернуть его было бы нечем: экрана настроек в приложении нет. */}
      <div className="faint install-footer">
        <Link to="/install">Как открывать с домашнего экрана</Link>
        <ForgetLogin />
      </div>
    </div>
  )
}
